import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { UserAuth } from '../context/AuthContext';
import { getAuth } from 'firebase/auth';
import { db } from "../firebase";
import { doc, collection, setDoc } from "firebase/firestore";
import { useNavigate, useParams } from 'react-router-dom';
import TsLogoWhite from '../assets/TSLogoWhite.svg';
import '../setupapp/setup.css';

const SignUp = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);

    const navigate = useNavigate();
    const params = useParams();
    const { createUser, verifyEmail } = UserAuth();
    const usersCollectionRef = collection(db, "users");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await createUser(email, password);
            await createUserAndNavigate();
        } catch (error) {
            setIsLoading(false);
            let friendlyMessage;
            switch (error.code) {
                case 'auth/email-already-in-use':
                    friendlyMessage = "This email is already registered. Please use a different email or sign in.";
                    break;
                case 'auth/invalid-email':
                    friendlyMessage = "Oops, that doesn't look like a valid email address.";
                    break;
                case 'auth/weak-password':
                    friendlyMessage = "Your password should be stronger. Please use a mix of letters, numbers, and symbols.";
                    break;
                default:
                    friendlyMessage = "Something went wrong. Please try again later.";
                    break;
            }
            setError(friendlyMessage);
        }
    };

    const handleSendVerificationEmail = async () => {
        const currentUser = getAuth().currentUser;
        if (currentUser) {
            try {
                await verifyEmail();
            } catch (error) {
                console.error("Error sending verification email: ", error);
            }
        }
    };

    const createUserAndNavigate = async () => {
        const newUser = {
            email: email,
            subscribeNewsletter: subscribeNewsletter
        };
        await setDoc(doc(usersCollectionRef), newUser);
        await handleSendVerificationEmail();

        if (params.navigation === "iceland") {
            navigate('/checkout/iceland');
        } else if (params.navigation === "checkout") {
            const pending = sessionStorage.getItem('pendingCheckoutData');
            if (pending) {
                const parsedState = JSON.parse(pending);
                sessionStorage.removeItem('pendingCheckoutData');
                navigate('/checkout', { state: parsedState });
            } else {
                navigate('/checkout');
            }
        } else {
            navigate(`/${params.navigation || 'aurora'}`);
        }
    };

    const isFormValid = email && email.includes('@') && password && acceptTerms && !isLoading;

    return (
        <div className="setup-app-container setup-page-with-header">
            <motion.div
                className="setup-glass-panel"
                initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: 10 }} 
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.3 }}
                style={{ perspective: 1000 }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                    style={{ textAlign: 'center' }}
                >
                    <img src={TsLogoWhite} alt="Tape Signal" style={{ width: '64px', marginBottom: '24px' }} />
                </motion.div>
                
                <motion.h1 
                    className="setup-header"
                    initial={{ opacity: 0, filter: 'blur(10px)' }} 
                    animate={{ opacity: 1, filter: 'blur(0px)' }} 
                    transition={{ delay: 0.3, duration: 0.8 }}
                >
                    {params.navigation === 'checkout' ? "Almost there." : "Create Account."}
                </motion.h1>
                <motion.p 
                    className="setup-subtitle"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                >
                    {params.navigation === 'checkout' 
                        ? "Create an account to secure your licenses and finish your purchase." 
                        : "Join the ecosystem and unlock your plugins."}
                </motion.p>

                <motion.form 
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
                >
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setFocusedInput('email')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocusedInput('password')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>

                    <div style={{ padding: '8px 4px', marginBottom: '16px' }}>
                        <div className="setup-checkbox-wrapper" onClick={() => setSubscribeNewsletter(!subscribeNewsletter)}>
                            <div className={`setup-checkbox-box ${subscribeNewsletter ? 'checked' : ''}`}>
                                {subscribeNewsletter && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                            </div>
                            <span className="setup-checkbox-label">Sign up for the newsletter</span>
                        </div>
                        <div className="setup-checkbox-wrapper" onClick={() => setAcceptTerms(!acceptTerms)}>
                            <div className={`setup-checkbox-box ${acceptTerms ? 'checked' : ''}`}>
                                {acceptTerms && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                            </div>
                            <span className="setup-checkbox-label">I accept the terms and conditions*</span>
                        </div>
                    </div>

                    <motion.button
                        type="submit"
                        className="setup-btn-primary"
                        disabled={!isFormValid}
                    >
                        {isLoading ? <div className="setup-loader-ring"></div> : "Sign Up"}
                    </motion.button>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                className="setup-error"
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                        style={{ textAlign: 'center', marginTop: '24px' }}
                    >
                        <button type="button" className="setup-link" onClick={() => navigate(params.navigation ? `/signin?nav=${params.navigation}` : '/signin')}>
                            Already have an account? Sign In
                        </button>
                    </motion.div>
                </motion.form>
            </motion.div>
        </div>
    );
};

export default SignUp;
